import { Button, Dialog, DialogActions, DialogContent, DialogHeading } from 'omakase';
import { IconPlusLarge } from '@tock/icons';
import { StoreType } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import Anchor from 'components/Anchor';
import BusinessPlaylistsDialogListsPage from 'components/profile/playlists/BusinessPlaylistsDialogListsPage';
import BusinessPlaylistsDialogNewListPage from 'components/profile/playlists/BusinessPlaylistsDialogListPage';
import BusinessPlaylistsDialogNotesPage from 'components/profile/playlists/BusinessPlaylistsDialogNotesPage';

interface PlaylistsDialogProps {
  isOpen: boolean;
  onClose(): void;
  business: core.IBusiness;
}

const viewListsButton = ({ breakpoints }: Theme) => css`
  margin-right: auto;
  display: none;

  ${breakpoints.up('sm')} {
    display: inline-flex;
  }
`;

const fullWidthMobile = ({ breakpoints }: Theme) => css`
  width: 100%;

  ${breakpoints.up('sm')} {
    width: auto;
  }
`;

const dialogActions = css`
  & > * {
    flex: 1;
  }
`;

const newListButtonMobile = ({ breakpoints }: Theme) => css`
  ${breakpoints.down('xs')} {
    margin: 0;
  }
`;

const BusinessPlaylistsDialog: React.FunctionComponent<PlaylistsDialogProps> = ({
  isOpen,
  onClose,
  business,
}) => {
  const { playlists, isInitialized } = useSelector((state: StoreType) => ({
    playlists: state.patron.playlists.data,
    isInitialized: state.patron.playlists.isInitialized,
  }));
  const [activePage, setActivePage] = useState<
    | { page: 'LISTS' }
    | { page: 'NEW_LIST' }
    | { page: 'NOTES'; playlist: consumer.Playlist; playlistItem: consumer.IPlaylistItem }
  >({ page: 'LISTS' });

  const getPage = () => {
    switch (activePage.page) {
      case 'LISTS':
        return (
          <DialogContent>
            <BusinessPlaylistsDialogListsPage
              onClickNote={(playlist, playlistItem) =>
                setActivePage({ page: 'NOTES', playlist, playlistItem })
              }
              playlists={playlists || []}
              business={business}
              isOpen={isOpen && !!isInitialized}
            />
          </DialogContent>
        );
      case 'NEW_LIST':
        return (
          <BusinessPlaylistsDialogNewListPage
            business={business}
            onSaveDone={() => setActivePage({ page: 'LISTS' })}
            wrapper={(content) => <div>{content}</div>}
          />
        );
      case 'NOTES':
        return (
          <DialogContent>
            <BusinessPlaylistsDialogNotesPage
              business={business}
              playlist={activePage.playlist}
              playlistItem={activePage.playlistItem}
            />
          </DialogContent>
        );
      default:
        assertNever(activePage);
    }
  };

  const getDialogActions = () => {
    switch (activePage.page) {
      case 'LISTS':
        return (
          <DialogActions css={dialogActions}>
            <Button
              size="large"
              variant="ghost"
              component={Anchor}
              href={'/profile/saved'}
              css={viewListsButton}
            >
              View Saved Lists
            </Button>
            <Button
              variant="basic"
              iconStart={<IconPlusLarge />}
              onClick={() => setActivePage({ page: 'NEW_LIST' })}
              size="large"
              css={newListButtonMobile}
            >
              New List
            </Button>
            <Button variant="primary" onClick={onClose} size="large">
              Done
            </Button>
          </DialogActions>
        );

      case 'NOTES':
        return (
          <DialogActions>
            <Button
              css={fullWidthMobile}
              variant="primary"
              onClick={() => setActivePage({ page: 'LISTS' })}
              size="large"
            >
              Done
            </Button>
          </DialogActions>
        );

      default:
        return null;
    }
  };

  const handleClose = () => {
    switch (activePage.page) {
      case 'LISTS':
        return onClose();

      case 'NOTES':
      case 'NEW_LIST':
        setActivePage({ page: 'LISTS' });
        return;
      default:
        assertNever(activePage);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth aria-label="Business Saved Lists">
      <DialogHeading
        onClose={
          activePage.page === 'NEW_LIST' || activePage.page === 'LISTS' ? handleClose : undefined
        }
      >
        Save to list
      </DialogHeading>
      {getPage()}
      {getDialogActions()}
    </Dialog>
  );
};

export default BusinessPlaylistsDialog;
